/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useMemo } from "react";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// react-table components
import { useTable, usePagination, useGlobalFilter, useSortBy } from "react-table";

// @mui material components
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Icon from "@mui/material/Icon";
import Autocomplete from "@mui/material/Autocomplete";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDPagination from "components/MDPagination";

// Material Dashboard 2 React example components
import DataTableHeadCell from "components/Tables/DataTable/DataTableHeadCell";
import DataTableBodyCell from "components/Tables/DataTable/DataTableBodyCell";
import MDInputRoot from "components/MDInput/MDInputRoot";

function DataTable({
  table,
  noEndBorder,
  setPaging,
  paging,
  paginate = true,
  hidePerPage = false
}) {
  const entries = ["5", "10", "15", "20", "25", "30"]
  const columns = useMemo(() => table.columns, [table]);
  const data = useMemo(() => table.rows, [table]);

  const tableInstance = useTable(
    { columns, data, initialState: { pageIndex: 0 }, pageCount: Math.ceil(paging?.total / paging?.perPage), manualPagination: true },
    useGlobalFilter,
    useSortBy,
    usePagination,
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    page,
    pageOptions,
  } = tableInstance;

  // Render the paginations
  const renderPagination = () => {
    const totalPages = pageOptions.length;

    if (totalPages <= 6) {
      // Render all pages if 6 or fewer
      return pageOptions.map((option) => (
        <MDPagination
          item
          key={option}
          onClick={() => setPaging((prev) => ({ ...prev, page: Number(option) + 1 }))}
          active={paging?.page === option + 1} // Explicitly check active state
        >
          {option + 1}
        </MDPagination>
      ));
    }

    const currentPage = paging?.page || 1;

    const paginationItems = [];

    // Always include the first page
    paginationItems.push(
      <MDPagination
        item
        key={1}
        onClick={() => setPaging((prev) => ({ ...prev, page: 1 }))}
        active={currentPage === 1} // Explicitly check active state
      >
        1
      </MDPagination>
    );

    // Add ellipsis before the current page block if needed
    if (currentPage > 4) {
      paginationItems.push(<MDTypography key="start-ellipsis" variant="caption">...</MDTypography>);
    }

    // Add current page and its neighbors
    for (let i = Math.max(currentPage - 1, 2); i <= Math.min(currentPage + 1, totalPages - 1); i++) {
      paginationItems.push(
        <MDPagination
          item
          key={i}
          onClick={() => setPaging((prev) => ({ ...prev, page: i }))}
          active={currentPage === i} // Explicitly check active state
        >
          {i}
        </MDPagination>
      );
    }

    // Add ellipsis after the current page block if needed
    if (currentPage < totalPages - 2) {
      paginationItems.push(<MDTypography key="end-ellipsis" variant="caption">...</MDTypography>);
    }

    // Always include the last page
    paginationItems.push(
      <MDPagination
        item
        key={totalPages}
        onClick={() => setPaging((prev) => ({ ...prev, page: totalPages }))}
        active={currentPage === totalPages} // Explicitly check active state
      >
        {totalPages}
      </MDPagination>
    );

    return paginationItems;
  };

  return (
    <TableContainer sx={{ boxShadow: "none" }}>

      <Table {...getTableProps()}>
        <MDBox component="thead">
          {headerGroups.map((headerGroup, key) => (
            <TableRow key={key} {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column, idx) => (
                <DataTableHeadCell
                  key={idx}
                  width={column.width ? column.width : "auto"}
                  align={column.align ? column.align : "left"}
                >
                  {column.render("Header")}
                </DataTableHeadCell>
              ))}
            </TableRow>
          ))}
        </MDBox>
        <TableBody {...getTableBodyProps()}>
          {page?.length === 0 &&
            <MDTypography variant="h6" color="secondary" style={{ margin: "18px" }}>
              Sem dados !
            </MDTypography>}
          {page.map((row, key) => {
            prepareRow(row);
            return (
              <TableRow key={key} {...row.getRowProps()}>
                {row.cells.map((cell, idx) => (
                  <DataTableBodyCell
                    key={idx}
                    noBorder={noEndBorder && rows.length - 1 === key}
                    align={cell.column.align ? cell.column.align : "left"}
                    {...cell.getCellProps()}
                  >
                    {cell.render("Cell")}
                  </DataTableBodyCell>
                ))}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {
        paginate &&

        <MDBox
          display="flex"
          flexDirection={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          p={3}
        >
          <MDBox display="flex" justifyContent="space-between" alignItems="center" pt={pageOptions.length > 1 ? 0 : 2} pl={pageOptions.length > 1 ? 0.3 : 3} pb={pageOptions.length > 1 ? 0 : 3} >
            <MDBox display="flex" alignItems="center">
              {!hidePerPage &&
                <>
                  <Autocomplete
                    disableClearable
                    value={paging?.perPage?.toString()}
                    options={entries}
                    onChange={(event, newValue) => {
                      setPaging(prev => ({ ...prev, page: 1, perPage: parseInt(newValue, 10) }))
                    }}
                    size="small"
                    sx={{ width: "5rem" }}
                    renderInput={(params) => <MDInputRoot {...params} />}
                  />
                  <MDTypography variant="caption" color="secondary">
                    &nbsp;&nbsp;Registos por página
                  </MDTypography>
                </>
              }
            </MDBox>
          </MDBox>
          {pageOptions.length > 1 && (
            <MDPagination
              variant={"gradient"}
              color={"info"}
            >
              {paging.page > 1 && (
                <MDPagination item onClick={() => setPaging((prev) => ({ ...prev, page: prev.page - 1 }))}>
                  <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
                </MDPagination>
              )}
              {renderPagination()}
              {Math.ceil(paging?.total / paging?.perPage) !== paging.page && (
                <MDPagination item onClick={() => setPaging((prev) => ({ ...prev, page: prev.page + 1 }))}>
                  <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
                </MDPagination>
              )}
            </MDPagination>
          )}

        </MDBox>
      }
    </TableContainer>
  );
}

// Setting default values for the props of DataTable
DataTable.defaultProps = {
  entriesPerPage: { defaultValue: 10, entries: [5, 10, 15, 20, 25] },
  canSearch: false,
  showTotalEntries: true,
  pagination: { variant: "gradient", color: "info" },
  isSorted: true,
  noEndBorder: false,
};

// Typechecking props for the DataTable
DataTable.propTypes = {
  entriesPerPage: PropTypes.oneOfType([
    PropTypes.shape({
      defaultValue: PropTypes.number,
      entries: PropTypes.arrayOf(PropTypes.number),
    }),
    PropTypes.bool,
  ]),
  canSearch: PropTypes.bool,
  showTotalEntries: PropTypes.bool,
  table: PropTypes.objectOf(PropTypes.array).isRequired,
  pagination: PropTypes.shape({
    variant: PropTypes.oneOf(["contained", "gradient"]),
    color: PropTypes.oneOf([
      "primary",
      "secondary",
      "info",
      "success",
      "warning",
      "error",
      "dark",
      "light",
    ]),
  }),
  isSorted: PropTypes.bool,
  noEndBorder: PropTypes.bool,
  hidePerPage: PropTypes.bool
};

export default DataTable;
